/**
 * alert.tsx — shadcn/ui Alert component.
 *
 * Used for inline page-level callouts (info, warning, error, success). Unlike
 * `toast`, an Alert persists in the page layout and is appropriate for
 * standing context like "this feature is standalone" explanations.
 *
 * Exports `Alert`, `AlertTitle`, `AlertDescription`. The `variant` prop
 * controls the color scheme: `default` (muted/info), `destructive` (red),
 * `success` (green), `warning` (amber).
 */
import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const alertVariants = cva(
  "relative w-full rounded-lg border px-4 py-3 text-sm [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground [&>svg~*]:pl-7",
  {
    variants: {
      variant: {
        default: "border-border bg-background text-foreground",
        destructive:
          "border-red-500/50 text-red-700 [&>svg]:text-red-700 dark:text-red-400",
        success:
          "border-emerald-500/50 text-emerald-700 [&>svg]:text-emerald-700 dark:text-emerald-400",
        warning:
          "border-amber-500/50 text-amber-700 [&>svg]:text-amber-700 dark:text-amber-400",
      },
    },
    defaultVariants: { variant: "default" },
  },
);

const Alert = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, ...props }, ref) => (
  <div ref={ref} role="alert" className={cn(alertVariants({ variant }), className)} {...props} />
));
Alert.displayName = "Alert";

const AlertTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn("mb-1 font-medium leading-none tracking-tight", className)}
    {...props}
  />
));
AlertTitle.displayName = "AlertTitle";

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("text-sm [&_p]:leading-relaxed", className)} {...props} />
));
AlertDescription.displayName = "AlertDescription";

export { Alert, AlertTitle, AlertDescription };
